import { useEffect, useRef, type ReactNode } from 'react';
import { iconMarkup } from './icons';

export function Icon({ name, className = '' }: { name: string; className?: string }) {
  // Trusted local vector paths from the delivery package, never external input.
  return (
    <svg
      className={`p01-icon ${className}`}
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: iconMarkup[name] ?? iconMarkup['info'] ?? '' }}
    />
  );
}
export function Card({
  zone,
  title,
  extra,
  children,
  className = '',
}: {
  zone: string;
  title: string;
  extra?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section id={zone} className={`p01-card ${className}`} aria-labelledby={`${zone}-title`}>
      <div className="p01-card-heading">
        <h2 id={`${zone}-title`}>{title}</h2>
        {extra}
      </div>
      {children}
    </section>
  );
}
export function Badge({ children, tone = 'blue' }: { children: ReactNode; tone?: string }) {
  return <span className={`p01-badge p01-${tone}`}>{children}</span>;
}
export function EventStatus({ state }: { state: string }) {
  return (
    <Badge tone={state === '处置中' ? 'red' : state === '已控制' ? 'green' : 'blue'}>{state}</Badge>
  );
}
export function Modal({
  title,
  children,
  close,
}: {
  title: string;
  children: ReactNode;
  close: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const previous = document.activeElement;
    const dialog = ref.current;
    dialog?.showModal();
    return () => {
      dialog?.close();
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, []);
  return (
    <dialog
      ref={ref}
      className="p01-dialog"
      aria-labelledby="p01-dialog-title"
      onCancel={close}
      onKeyDown={(event) => {
        if (event.key !== 'Tab') return;
        const controls = event.currentTarget.querySelectorAll<HTMLElement>(
          'button:not(:disabled), a[href], input:not(:disabled), [tabindex="0"]',
        );
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          const box = event.currentTarget.getBoundingClientRect();
          if (
            event.clientX < box.left ||
            event.clientX > box.right ||
            event.clientY < box.top ||
            event.clientY > box.bottom
          )
            close();
        }
      }}
    >
      <div className="p01-dialog-heading">
        <h2 id="p01-dialog-title">{title}</h2>
        <button autoFocus aria-label="关闭查看" onClick={close}>
          <Icon name="close" />
        </button>
      </div>
      {children}
      <div className="p01-dialog-footer">
        <button onClick={close}>关闭查看</button>
      </div>
    </dialog>
  );
}
